import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import {
  Subject,
  catchError,
  distinctUntilChanged,
  map,
  merge,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { CarbonTable } from 'src/app/components/carbon-table/carbon-table.component';
import { PositionCustody } from 'src/app/shared/interfaces/position-custody';
import { Pagination } from 'src/app/shared/interfaces/pagination';

import { PositionCustodyFilter } from '../interfaces/position-custody-filter';
import { PositionConsultService } from '../services/position-consult.service';
import { PositionConsultListService } from '../shared/position-consult-list.service';
import {
  countActiveFilters,
  createPositionConsultPagination,
  displayPositionValue,
  hasQueryableFilters,
  resolvePositionComitenteLabel,
  resolvePositionSerialLabel,
} from '../shared/position-consult.utils';

type PositionQuerySource = 'filter' | 'page' | 'retry';

@Component({
  standalone: false,
  selector: 'app-position-consult-list',
  templateUrl: './position-consult-list.component.html',
  styleUrls: ['./position-consult-list.component.scss'],
})
export class PositionConsultListComponent extends CarbonTable<PositionCustody> implements OnInit, OnDestroy {
  private static readonly PAGE_SIZE = 10;
  private static readonly REQUEST_OPTIONS = { suppressLegacyErrorModal: true };

  @Output() positionDetail = new EventEmitter<PositionCustody>();
  @Output() valueFilterTotal = new EventEmitter<number>();
  @Output() valueTotal = new EventEmitter<number>();
  @Output() loadFailed = new EventEmitter<void>();

  /** Quebras explícitas iguais ao Figma (white-space: pre-line no SCSS). */
  readonly headerAccountLabel = 'Conta do\nDetentor';
  readonly headerInstrumentLabel = 'Código do\nInst. Finan.';
  readonly headerProjectLabel = 'ID do\nProjeto';

  private readonly destroy$ = new Subject<void>();
  private readonly queryTrigger$ = new Subject<PositionQuerySource>();
  private lastSuccessfulPage = 1;
  private hasQueried = false;

  override pagination: Pagination<PositionCustody> = createPositionConsultPagination(
    PositionConsultListComponent.PAGE_SIZE,
  );

  constructor(
    private readonly positionService: PositionConsultService,
    private readonly positionConsultListService: PositionConsultListService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.setupQueryPipeline();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.queryTrigger$.complete();
  }

  get positionsFoundLabel(): string {
    if (!this.hasQueried) {
      return 'Aplique um filtro para consultar a posição';
    }

    const count = this.totalItens ?? 0;
    const hasActiveFilters = countActiveFilters(
      this.positionConsultListService.filters.getValue(),
    ) > 0;

    if (hasActiveFilters) {
      return count === 1
        ? '1 resultado encontrado para o filtro selecionado'
        : `${count} resultados encontrados para o filtro selecionado`;
    }

    return count === 1
      ? '1 resultado encontrado'
      : `${count} resultados encontrados`;
  }

  get recordsPerPageLabel(): string {
    const pageSize = this.pagination.size ?? PositionConsultListComponent.PAGE_SIZE;
    return pageSize === 1
      ? 'Mostrando 1 registro por página'
      : `Mostrando ${pageSize} registros por página`;
  }

  trackByPosition(_index: number, position: PositionCustody): string {
    return `${position.id ?? position.carbonCreditCode ?? 'if'}-${position.accountCode ?? 'acc'}-${_index}`;
  }

  getComitenteLabel(position: PositionCustody): string {
    return resolvePositionComitenteLabel(position);
  }

  getSerialLabel(position: PositionCustody): string {
    return resolvePositionSerialLabel(position);
  }

  getProjectIdLabel(position: PositionCustody): string {
    return displayPositionValue(position.projectId);
  }

  getPositionQuantityLabel(position: PositionCustody): string {
    return displayPositionValue(position.creditQnty);
  }

  getDetailsLabel(position: PositionCustody): string {
    return `Visualizar detalhes do crédito ${position.carbonCreditCode || ''}`.trim();
  }

  onPaginationChange(event: Event): void {
    const customEvent = event as CustomEvent<{ current: number; previous: number }>;
    const nextPage = customEvent.detail?.current;

    if (nextPage && nextPage !== this.pagination.page) {
      this.onPageChange(nextPage);
    }
  }

  onPageChange(currentPage: number): void {
    if (this.totalPages > 0 && currentPage > this.totalPages) {
      return;
    }

    this.pagination.page = currentPage;
    this.queryTrigger$.next('page');
  }

  override resetPagination(): void {
    this.pagination = createPositionConsultPagination(
      PositionConsultListComponent.PAGE_SIZE,
    );
  }

  openDetail(row: PositionCustody): void {
    this.positionDetail.emit(row);
  }

  reloadCurrentQuery(): void {
    this.queryTrigger$.next('retry');
  }

  private setupQueryPipeline(): void {
    const filterChanges$ = this.positionConsultListService.filters.pipe(
      distinctUntilChanged(
        (previous, current) => JSON.stringify(previous) === JSON.stringify(current),
      ),
      tap(() => this.resetPagination()),
      map((filters) => ({ source: 'filter' as const, filters })),
    );

    const manualTriggers$ = this.queryTrigger$.pipe(
      map((source) => ({
        source,
        filters: this.positionConsultListService.filters.getValue(),
      })),
    );

    merge(filterChanges$, manualTriggers$).pipe(
      switchMap(({ source, filters }) => this.runQuery(source, filters)),
      takeUntil(this.destroy$),
    ).subscribe((response) => {
      if (!response) {
        return;
      }

      this.applyQueryResult(
        response.results ?? [],
        response.total ?? 0,
        response.totalPages,
      );
    });
  }

  private runQuery(source: PositionQuerySource, filters: PositionCustodyFilter) {
    this.emitFilterTotal(filters);

    if (!hasQueryableFilters(filters)) {
      this.hasQueried = false;
      this.applyQueryResult([], 0);
      return of(null);
    }

    this.hasQueried = true;

    return this.positionService.getPosition(
      filters,
      this.pagination,
      PositionConsultListComponent.REQUEST_OPTIONS,
    ).pipe(
      catchError(() => {
        if (source === 'page') {
          this.pagination.page = this.lastSuccessfulPage;
        } else {
          this.applyQueryResult([], 0);
        }

        this.loadFailed.emit();
        return of(null);
      }),
    );
  }

  private applyQueryResult(
    results: PositionCustody[],
    total: number,
    totalPages?: number,
  ): void {
    this.itens = results;
    this.totalItens = total;
    this.updateTotalPages(totalPages);
    this.lastSuccessfulPage = this.pagination.page ?? 1;
    this.valueTotal.emit(this.totalItens);
  }

  private updateTotalPages(totalPages?: number): void {
    if (totalPages != null && totalPages >= 0) {
      this.totalPages = totalPages;
      return;
    }

    this.handleTotalPages(null);
  }

  private emitFilterTotal(filters: PositionCustodyFilter): void {
    setTimeout(() => {
      this.valueFilterTotal.emit(countActiveFilters(filters));
    });
  }
}
